import { useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import type { AssetKind, AssetRecord } from '@shared/types';
import { inferAssetKind } from '@shared/utils/assets';
import loaderIcon from '@renderer/assets/icons/animated/loader.svg';
import { Icon } from '@renderer/shared/components/icons';
import {
  EditorTabs,
  EmptyState,
  IconButton,
  PanelCard,
  SelectField,
  Spinner,
  TextAreaField,
  TextField,
  ValuePair
} from '@renderer/shared/components/primitives';
import { useI18n } from '@renderer/shared/i18n';
import { getAssetPreviewUrl as getSharedAssetPreviewUrl } from '@renderer/shared/utils/asset-url';
import { areDraftsEqual } from '@renderer/shared/utils/draft-equality';
import { useUiStore } from '@renderer/stores/ui-store';
import { deleteAssets, importAssets, listAssets, updateAsset } from '../resources-repository';
import { ResourceCard } from './resource-card';
import styles from './project-resources-modal.module.css';

type ResourceCategoryFilter = 'all' | 'documents' | 'images' | 'icons';
type ResourceSort = 'recent' | 'oldest' | 'name_asc' | 'name_desc';

interface ProjectResourcesModalProps {
  isOpen: boolean;
  projectId: string;
  projectName: string;
  customerId: string | null;
  initialAssets: AssetRecord[];
  onClose: () => void;
  onAssetsChanged: () => Promise<void>;
}

interface PendingImport {
  id: string;
  name: string;
  kind: AssetKind;
}

interface AssetDraft {
  name: string;
  altText: string;
  description: string;
}

function cn(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(' ');
}

function toAssetDraft(asset: AssetRecord): AssetDraft {
  return {
    name: asset.name,
    altText: asset.altText ?? '',
    description: asset.description ?? ''
  };
}

function getAssetPreviewUrl(asset: AssetRecord): string | null {
  if (!asset.storagePath) {
    return null;
  }

  if (asset.kind !== 'image' && asset.kind !== 'icon' && asset.kind !== 'logo') {
    return null;
  }

  return getSharedAssetPreviewUrl(asset.id);
}

function getCategoryKinds(category: ResourceCategoryFilter): AssetKind[] {
  switch (category) {
    case 'documents':
      return ['document', 'pdf'];
    case 'images':
      return ['image'];
    case 'icons':
      return ['icon', 'logo'];
    default:
      return ['logo', 'icon', 'document', 'image', 'pdf'];
  }
}

function getAssetKindIcon(kind: AssetKind): Parameters<typeof Icon>[0]['name'] {
  switch (kind) {
    case 'image':
      return 'images';
    case 'pdf':
    case 'document':
      return 'document';
    case 'logo':
    case 'icon':
      return 'icon';
    default:
      return 'document';
  }
}

function getPlaceholderTone(kind: AssetKind): 'warm' | 'cool' | 'blue' | 'neutral' {
  if (kind === 'pdf') {
    return 'warm';
  }

  if (kind === 'image') {
    return 'cool';
  }

  if (kind === 'icon' || kind === 'logo') {
    return 'blue';
  }

  return 'neutral';
}

function matchesSearch(asset: AssetRecord, query: string): boolean {
  if (!query) {
    return true;
  }

  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return true;
  }

  return [asset.name, asset.altText ?? '', asset.description ?? ''].some((value) =>
    value.toLowerCase().includes(normalizedQuery)
  );
}

function sortAssets(assets: AssetRecord[], sort: ResourceSort): AssetRecord[] {
  return [...assets].sort((left, right) => {
    switch (sort) {
      case 'oldest':
        return new Date(left.updatedAt).getTime() - new Date(right.updatedAt).getTime();
      case 'name_asc':
        return left.name.localeCompare(right.name);
      case 'name_desc':
        return right.name.localeCompare(left.name);
      case 'recent':
      default:
        return new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime();
    }
  });
}

export function ProjectResourcesModal({
  isOpen,
  projectId,
  projectName,
  customerId,
  initialAssets,
  onClose,
  onAssetsChanged
}: ProjectResourcesModalProps): JSX.Element | null {
  const { locale, t } = useI18n();
  const addToast = useUiStore((state) => state.addToast);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dragDepthRef = useRef(0);
  const [workspaceAssets, setWorkspaceAssets] = useState<AssetRecord[]>(initialAssets);
  const [query, setQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<ResourceCategoryFilter>('all');
  const [sort, setSort] = useState<ResourceSort>('recent');
  const [activeAssetId, setActiveAssetId] = useState<string | null>(initialAssets[0]?.id ?? null);
  const [draft, setDraft] = useState<AssetDraft | null>(initialAssets[0] ? toAssetDraft(initialAssets[0]) : null);
  const [pendingImports, setPendingImports] = useState<PendingImport[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDropActive, setIsDropActive] = useState(false);
  const [deleteIntentAssetId, setDeleteIntentAssetId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const projectAssets = useMemo(
    () => workspaceAssets.filter((asset) => asset.projectId === projectId && asset.deletedAt === null),
    [projectId, workspaceAssets]
  );

  const visibleAssets = useMemo(() => {
    const allowedKinds = new Set(getCategoryKinds(categoryFilter));

    return sortAssets(
      projectAssets.filter((asset) => allowedKinds.has(asset.kind) && matchesSearch(asset, query)),
      sort
    );
  }, [categoryFilter, projectAssets, query, sort]);

  const activeAsset = useMemo(
    () => projectAssets.find((asset) => asset.id === activeAssetId) ?? null,
    [activeAssetId, projectAssets]
  );

  const updatedLabel = useMemo(() => {
    if (!activeAsset) {
      return null;
    }

    return new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short', year: 'numeric' }).format(
      new Date(activeAsset.updatedAt)
    );
  }, [activeAsset, locale]);

  const isDirty =
    activeAsset && draft ? !areDraftsEqual(draft, toAssetDraft(activeAsset)) : false;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const nextProjectAssets = initialAssets.filter((asset) => asset.projectId === projectId && asset.deletedAt === null);
    setWorkspaceAssets(initialAssets);
    setQuery('');
    setCategoryFilter('all');
    setSort('recent');
    setActiveAssetId(nextProjectAssets[0]?.id ?? null);
    setDraft(nextProjectAssets[0] ? toAssetDraft(nextProjectAssets[0]) : null);
    setPendingImports([]);
    setIsDropActive(false);
    setDeleteIntentAssetId(null);
    setError(null);
    setIsSaving(false);
    setIsDeleting(false);

    let active = true;
    setIsLoading(true);

    void listAssets()
      .then((collection) => {
        if (!active) {
          return;
        }

        setWorkspaceAssets(collection.assets);
      })
      .catch((nextError) => {
        if (active) {
          setError(nextError instanceof Error ? nextError.message : t('resources.projectModal.messages.loadError'));
        }
      })
      .finally(() => {
        if (active) {
          setIsLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [isOpen, projectId, t]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const nextProjectAssets = initialAssets.filter((asset) => asset.projectId === projectId && asset.deletedAt === null);
    setWorkspaceAssets((current) => {
      const nextAssetsById = new Map<string, AssetRecord>();

      for (const asset of current) {
        if (asset.projectId !== projectId) {
          nextAssetsById.set(asset.id, asset);
        }
      }

      for (const asset of nextProjectAssets) {
        nextAssetsById.set(asset.id, asset);
      }

      return [...nextAssetsById.values()];
    });
  }, [initialAssets, isOpen, projectId]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (visibleAssets.length === 0) {
      if (activeAssetId !== null) {
        setActiveAssetId(null);
      }
      return;
    }

    if (!activeAssetId || !visibleAssets.some((asset) => asset.id === activeAssetId)) {
      setActiveAssetId(visibleAssets[0]?.id ?? null);
    }
  }, [activeAssetId, isOpen, visibleAssets]);

  useEffect(() => {
    if (!activeAsset) {
      setDraft(null);
      setDeleteIntentAssetId(null);
      return;
    }

    setDraft(toAssetDraft(activeAsset));
    setDeleteIntentAssetId((current) => (current === activeAsset.id ? current : null));
  }, [activeAsset]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      if (!isSaving && !isDeleting) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isDeleting, isOpen, isSaving, onClose]);

  if (!isOpen) {
    return null;
  }

  const categoryTabs = [
    { id: 'all' as const, label: t('resources.categories.all'), icon: 'all' as const },
    { id: 'documents' as const, label: t('resources.categories.documents'), icon: 'document' as const },
    { id: 'images' as const, label: t('resources.categories.images'), icon: 'images' as const },
    { id: 'icons' as const, label: t('resources.categories.icons'), icon: 'icon' as const }
  ];

  const sortOptions = [
    { value: 'recent', label: t('resources.sort.recent') },
    { value: 'oldest', label: t('resources.sort.oldest') },
    { value: 'name_asc', label: t('resources.sort.nameAsc') },
    { value: 'name_desc', label: t('resources.sort.nameDesc') }
  ];

  async function refreshAssetsFromWorkspace(): Promise<void> {
    const collection = await listAssets();
    setWorkspaceAssets(collection.assets);
  }

  async function handleImportFiles(files: File[]): Promise<void> {
    if (files.length === 0) {
      return;
    }

    setError(null);
    const previousProjectAssetIds = new Set(projectAssets.map((asset) => asset.id));
    const nextPendingImports = files.map((file) => ({
      id: typeof crypto?.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
      name: file.name,
      kind: inferAssetKind(file.name, file.type || null)
    }));

    setPendingImports(nextPendingImports);

    try {
      const items = await Promise.all(
        files.map(async (file) => {
          const fileWithPath = file as File & { path?: string };
          const sourcePath = typeof fileWithPath.path === 'string' ? fileWithPath.path : null;

          if (sourcePath) {
            return {
              fileName: file.name,
              mimeType: file.type || null,
              sourcePath
            };
          }

          const buffer = await file.arrayBuffer();
          return {
            fileName: file.name,
            mimeType: file.type || null,
            bytes: new Uint8Array(buffer)
          };
        })
      );

      const nextCollection = await importAssets({
        items,
        projectId,
        customerId
      });

      setWorkspaceAssets(nextCollection.assets);
      const nextProjectAssets = nextCollection.assets.filter(
        (asset) => asset.projectId === projectId && asset.deletedAt === null
      );
      const firstNewAsset = nextProjectAssets.find((asset) => !previousProjectAssetIds.has(asset.id)) ?? null;

      if (firstNewAsset) {
        setActiveAssetId(firstNewAsset.id);
      }

      await onAssetsChanged();
      addToast({
        title: t('resources.projectModal.notifications.uploadSuccessTitle'),
        message: t('resources.projectModal.notifications.uploadSuccessMessage', { count: files.length }),
        tone: 'success'
      });
    } catch (nextError) {
      const message = nextError instanceof Error ? nextError.message : t('resources.projectModal.messages.uploadError');
      setError(message);
      addToast({
        title: t('resources.projectModal.notifications.uploadErrorTitle'),
        message,
        tone: 'danger'
      });
      try {
        await refreshAssetsFromWorkspace();
      } catch {
        // Keep the local snapshot if the workspace refresh also fails.
      }
    } finally {
      setPendingImports([]);
    }
  }

  async function handleUpload(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const files = event.target.files ? Array.from(event.target.files) : [];
    event.target.value = '';
    await handleImportFiles(files);
  }

  async function handleSaveAsset(): Promise<void> {
    if (!activeAsset || !draft) {
      return;
    }

    if (!draft.name.trim()) {
      setError(t('resources.modal.validationName'));
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const nextDetail = await updateAsset({
        id: activeAsset.id,
        name: draft.name.trim(),
        altText: draft.altText.trim() ? draft.altText.trim() : null,
        description: draft.description.trim() ? draft.description.trim() : null
      });

      setWorkspaceAssets((current) =>
        current.map((asset) => (asset.id === nextDetail.asset.id ? nextDetail.asset : asset))
      );
      setDraft(toAssetDraft(nextDetail.asset));
      await onAssetsChanged();
      addToast({
        title: t('resources.projectModal.notifications.saveSuccessTitle'),
        message: t('resources.projectModal.notifications.saveSuccessMessage'),
        tone: 'success'
      });
    } catch (nextError) {
      const message = nextError instanceof Error ? nextError.message : t('resources.projectModal.messages.saveError');
      setError(message);
      addToast({
        title: t('resources.projectModal.notifications.saveErrorTitle'),
        message,
        tone: 'danger'
      });
      try {
        await refreshAssetsFromWorkspace();
      } catch {
        // Leave the current form state visible when the workspace refresh fails.
      }
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDeleteAsset(): Promise<void> {
    if (!activeAsset) {
      return;
    }

    if (deleteIntentAssetId !== activeAsset.id) {
      setDeleteIntentAssetId(activeAsset.id);
      return;
    }

    setIsDeleting(true);
    setError(null);

    try {
      const nextCollection = await deleteAssets({ ids: [activeAsset.id] });
      setWorkspaceAssets(nextCollection.assets);
      setDeleteIntentAssetId(null);
      await onAssetsChanged();
      addToast({
        title: t('resources.projectModal.notifications.deleteSuccessTitle'),
        message: t('resources.projectModal.notifications.deleteSuccessMessage'),
        tone: 'success'
      });
    } catch (nextError) {
      const message = nextError instanceof Error ? nextError.message : t('resources.projectModal.messages.deleteError');
      setError(message);
      addToast({
        title: t('resources.projectModal.notifications.deleteErrorTitle'),
        message,
        tone: 'danger'
      });
      try {
        await refreshAssetsFromWorkspace();
      } catch {
        // Keep the current library state if the workspace refresh fails.
      }
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <div
      className={styles.overlay}
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget && !isSaving && !isDeleting) {
          onClose();
        }
      }}
    >
      <div
        aria-modal="true"
        aria-labelledby="project-resources-modal-title"
        className={styles.dialog}
        role="dialog"
        onClick={(event) => event.stopPropagation()}
      >
        <header className={styles.header}>
          <div className={styles.headerCopy}>
            <div className={styles.headerTitleRow}>
              <h2 className={styles.headerTitle} id="project-resources-modal-title">
                {t('resources.projectModal.title')}
              </h2>
              <span className={styles.scopeBadge}>
                <Icon name="projects" />
                <span>{t('resources.projectModal.projectScope')}</span>
              </span>
            </div>
            <p className={styles.headerDescription}>
              {t('resources.projectModal.description', { projectName })}
            </p>
          </div>
          <IconButton icon="times" label={t('common.close')} onClick={onClose} disabled={isSaving || isDeleting} />
        </header>

        <div className={styles.toolbar}>
          <div className={styles.toolbarMain}>
            <div className={cn('av-page-search', 'av-page-search--compact', styles.search)}>
              <Icon name="search" />
              <TextField
                aria-label={t('resources.projectModal.searchPlaceholder')}
                className="av-page-search__input"
                unstyled
                value={query}
                onValueChange={setQuery}
                placeholder={t('resources.projectModal.searchPlaceholder')}
              />
            </div>
            <div className={styles.categoryTabs}>
              <EditorTabs tabs={categoryTabs} value={categoryFilter} onChange={setCategoryFilter} />
            </div>
          </div>

          <SelectField
            aria-label={t('resources.projectModal.sortLabel')}
            value={sort}
            options={sortOptions}
            onChange={(event) => setSort(event.target.value as ResourceSort)}
          />

          <div className={styles.toolbarActions}>
            <input
              ref={fileInputRef}
              className="av-file-input"
              type="file"
              multiple
              onChange={(event) => void handleUpload(event)}
            />
            <button className="av-secondary-button" type="button" onClick={() => fileInputRef.current?.click()}>
              <Icon name="plus" />
              <span>{t('resources.actions.upload')}</span>
            </button>
          </div>
        </div>

        {error ? <div className="av-inline-error">{error}</div> : null}

        <div className={styles.body}>
          <PanelCard
            className={styles.libraryPanel}
            onDragEnter={(event) => {
              event.preventDefault();
              dragDepthRef.current += 1;
              setIsDropActive(true);
            }}
            onDragOver={(event) => {
              event.preventDefault();
              if (event.dataTransfer) {
                event.dataTransfer.dropEffect = 'copy';
              }
            }}
            onDragLeave={(event) => {
              event.preventDefault();
              dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
              if (dragDepthRef.current === 0) {
                setIsDropActive(false);
              }
            }}
            onDrop={(event) => {
              event.preventDefault();
              dragDepthRef.current = 0;
              setIsDropActive(false);
              void handleImportFiles(Array.from(event.dataTransfer.files ?? []));
            }}
          >
            {isDropActive ? (
              <div className="av-resource-drop-overlay" aria-hidden>
                <strong>{t('resources.upload.dropTitle')}</strong>
                <span>{t('resources.upload.dropSubtitle')}</span>
              </div>
            ) : null}

            <div className={styles.librarySummary}>
              <span>{t('resources.projectModal.resultsCount', { count: visibleAssets.length })}</span>
              {isLoading ? <Spinner size="small" label={t('resources.projectModal.loading')} /> : null}
            </div>

            <div className={styles.libraryScroll}>
              {visibleAssets.length === 0 && pendingImports.length === 0 ? (
                <div className={styles.emptyLibrary}>
                  <EmptyState
                    title={t('resources.projectModal.emptyTitle')}
                    description={t('resources.projectModal.emptyDescription')}
                  />
                </div>
              ) : (
                <div className="av-resource-grid">
                  {pendingImports.map((pending) => (
                    <article key={pending.id} className="av-resource-card is-pending">
                      <div className="av-resource-card__preview">
                        <div
                          className={[
                            'av-resource-card__preview-fallback',
                            pending.kind === 'pdf' ? 'is-placeholder-warm' : '',
                            pending.kind === 'icon' || pending.kind === 'logo' ? 'is-placeholder-blue' : '',
                            pending.kind === 'image' ? 'is-placeholder-cool' : ''
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          aria-hidden
                        >
                          <Icon name={getAssetKindIcon(pending.kind)} />
                        </div>
                        <div className="av-resource-card__loader" aria-hidden>
                          <img alt="" src={loaderIcon} />
                        </div>
                      </div>
                      <div className="av-resource-card__body">
                        <div className="av-resource-card__title-row">
                          <span className="av-resource-card__title">{pending.name}</span>
                          <span className="av-type-pill">{t(`enums.assetKind.${pending.kind}`)}</span>
                        </div>
                        <div className="av-resource-card__meta">
                          <Icon name="timer" />
                          <span>{t('resources.upload.importing')}</span>
                        </div>
                      </div>
                    </article>
                  ))}

                  {visibleAssets.map((asset) => (
                    <ResourceCard
                      key={asset.id}
                      id={asset.id}
                      name={asset.name}
                      kind={asset.kind}
                      kindLabel={t(`enums.assetKind.${asset.kind}`)}
                      metaLabel={
                        new Intl.DateTimeFormat(locale, {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        }).format(new Date(asset.updatedAt))
                      }
                      previewUrl={getAssetPreviewUrl(asset)}
                      selected={asset.id === activeAssetId}
                      onSelect={setActiveAssetId}
                      onOpenDetails={setActiveAssetId}
                    />
                  ))}
                </div>
              )}
            </div>
          </PanelCard>

          <PanelCard
            className={styles.inspectorPanel}
            title={t('resources.projectModal.inspectorTitle')}
          >
            {!activeAsset ? (
              <div className={styles.inspectorEmpty}>
                <EmptyState
                  title={t('resources.projectModal.inspectorEmptyTitle')}
                  description={t('resources.projectModal.inspectorEmptyDescription')}
                />
              </div>
            ) : (
              <div className={styles.inspectorBody}>
                <div className={styles.inspectorPreview}>
                  {getAssetPreviewUrl(activeAsset) ? (
                    <img src={getAssetPreviewUrl(activeAsset) ?? ''} alt={draft?.altText ?? ''} />
                  ) : (
                    <div
                      className={cn(
                        styles.inspectorPreviewFallback,
                        getPlaceholderTone(activeAsset.kind) === 'warm' && styles.isWarm,
                        getPlaceholderTone(activeAsset.kind) === 'cool' && styles.isCool,
                        getPlaceholderTone(activeAsset.kind) === 'blue' && styles.isBlue
                      )}
                      aria-hidden
                    >
                      <Icon name={getAssetKindIcon(activeAsset.kind)} />
                    </div>
                  )}
                </div>

                <div className={styles.metaGrid}>
                  <ValuePair label={t('quickAdd.fields.category')} value={t(`enums.assetKind.${activeAsset.kind}`)} />
                  <ValuePair label={t('resources.modal.updatedAt')} value={updatedLabel ?? t('common.notSet')} />
                  <ValuePair label={t('quickAdd.fields.project')} value={projectName} />
                  <ValuePair label={t('quickAdd.fields.mimeType')} value={activeAsset.mimeType ?? t('common.notSet')} />
                </div>

                <div className={styles.form}>
                  <label className="av-form-field">
                    <span>{t('quickAdd.fields.name')}</span>
                    <TextField
                      value={draft?.name ?? ''}
                      disabled={isSaving || isDeleting}
                      onValueChange={(value) =>
                        setDraft((current) => (current ? { ...current, name: value } : current))
                      }
                    />
                  </label>

                  <label className="av-form-field">
                    <span>{t('resources.modal.fields.altText')}</span>
                    <TextField
                      value={draft?.altText ?? ''}
                      disabled={isSaving || isDeleting}
                      onValueChange={(value) =>
                        setDraft((current) => (current ? { ...current, altText: value } : current))
                      }
                    />
                  </label>

                  <label className="av-form-field">
                    <span>{t('resources.modal.fields.description')}</span>
                    <TextAreaField
                      className="av-textarea"
                      rows={5}
                      value={draft?.description ?? ''}
                      disabled={isSaving || isDeleting}
                      onValueChange={(value) => setDraft((current) => (current ? { ...current, description: value } : current))}
                    />
                  </label>
                </div>

                {deleteIntentAssetId === activeAsset.id ? (
                  <div className={styles.deleteNotice}>
                    <strong>{t('resources.projectModal.confirmDeleteTitle')}</strong>
                    <p>{t('resources.projectModal.confirmDeleteMessage')}</p>
                    <div className={styles.deleteNoticeActions}>
                      <button
                        className="av-secondary-button"
                        type="button"
                        onClick={() => setDeleteIntentAssetId(null)}
                        disabled={isDeleting}
                      >
                        {t('common.cancel')}
                      </button>
                      <button className="av-primary-button" type="button" onClick={() => void handleDeleteAsset()} disabled={isDeleting}>
                        {isDeleting ? t('resources.projectModal.actions.removing') : t('resources.projectModal.actions.confirmRemove')}
                      </button>
                    </div>
                  </div>
                ) : null}

                <div className={styles.actions}>
                  <button
                    className="av-secondary-button"
                    type="button"
                    onClick={() => setDeleteIntentAssetId(activeAsset.id)}
                    disabled={isSaving || isDeleting}
                  >
                    <Icon name="trash" />
                    <span>{t('resources.projectModal.actions.remove')}</span>
                  </button>
                  <button
                    className="av-primary-button"
                    type="button"
                    onClick={() => void handleSaveAsset()}
                    disabled={!isDirty || isSaving || isDeleting}
                  >
                    {isSaving ? t('resources.projectModal.actions.saving') : t('common.applyChanges')}
                  </button>
                </div>
              </div>
            )}
          </PanelCard>
        </div>
      </div>
    </div>
  );
}
